/**
 * FdrPlanView — "Your vendor audit year."
 *
 * The FDR twin of the core Audit app's plan view. Planned desk, virtual, and
 * on-site vendor audits (fdr_oversight_activities) grouped by quarter, each
 * one click from becoming a real engagement in the shared audit engine —
 * grounded in the FDR authorities and the vendor's own delegated functions.
 * Below the plan: every active vendor with NO audit on this year's plan, so
 * the coverage gap is impossible to miss.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { STAGE_LABELS } from '../Audit/labels.js';

const API = process.env.REACT_APP_API_URL;
const TYPE_LABEL = { desk_audit: 'Desk audit', virtual_audit: 'Virtual audit', site_visit: 'Site visit' };
const RISK_STYLE = {
  critical: 'bg-red-50 text-red-700 border-red-200',
  high: 'bg-orange-50 text-orange-700 border-orange-200',
  medium: 'bg-amber-50 text-amber-700 border-amber-200',
  low: 'bg-slate-50 text-slate-500 border-slate-200',
};

const quarterOf = (d) => {
  if (!d) return 'Q9';
  const m = Number(String(d).slice(5, 7));
  return m ? `Q${Math.ceil(m / 3)}` : 'Q9';
};
const QUARTERS = [['Q1', 'January – March'], ['Q2', 'April – June'], ['Q3', 'July – September'], ['Q4', 'October – December'], ['Q9', 'Not yet dated']];
const fmtDate = (d) => {
  const m = String(d || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[2]}/${m[3]}/${m[1]}` : '';
};

export default function FdrPlanView({ onOpenAudit }) {
  const year = new Date().getFullYear();
  const [plan, setPlan] = useState({ planned: [], unplanned: [] });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState('');
  const [scheduling, setScheduling] = useState(null);   // entity being scheduled

  const load = useCallback(async () => {
    try {
      const r = await fetch(`${API}/api/fdr/audit-plan?year=${year}`, { credentials: 'include' }).then(r => r.json());
      if (r.success) setPlan({ planned: r.planned || [], unplanned: r.unplanned || [] });
    } catch { /* keep prior */ }
    setLoading(false);
  }, [year]);
  useEffect(() => { load(); }, [load]);

  const start = async (activityId) => {
    setBusy(activityId); setError('');
    try {
      const r = await fetch(`${API}/api/fdr/audit-plan/${activityId}/start`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: '{}',
      }).then(r => r.json());
      if (!r.success) throw new Error(r.message || 'Could not start the audit');
      onOpenAudit(r.audit_id);
    } catch (e) { setError(e.message); }
    setBusy(null);
  };

  const schedule = async (entityId, activityType, scheduledDate) => {
    setBusy(entityId); setError('');
    try {
      const r = await fetch(`${API}/api/fdr/audit-plan/schedule`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ entity_id: entityId, activity_type: activityType, scheduled_date: scheduledDate || null }),
      }).then(r => r.json());
      if (!r.success) throw new Error(r.message || 'Could not schedule');
      setScheduling(null);
      await load();
    } catch (e) { setError(e.message); }
    setBusy(null);
  };

  if (loading) return <div className="text-sm text-slate-400 py-6">Loading your vendor audit year…</div>;

  const byQuarter = QUARTERS
    .map(([q, label]) => [q, label, plan.planned.filter(a => quarterOf(a.scheduled_date) === q)])
    .filter(([, , list]) => list.length);
  const done = plan.planned.filter(a => a.audit_stage === 'closed' || a.completed_date).length;

  return (
    <div className="space-y-5">
      <div>
        <div className="text-base font-bold text-slate-800">Your vendor audit year</div>
        <div className="text-[12px] text-slate-400">
          {plan.planned.length
            ? <>Every vendor audit on the {year} plan. {done} of {plan.planned.length} completed.</>
            : <>No vendor audits planned for {year} yet — schedule one from the list below.</>}
        </div>
      </div>

      {error && <div className="text-xs text-red-600 bg-red-50 rounded-lg px-3 py-2">{error}</div>}

      {byQuarter.map(([q, label, list]) => (
        <div key={q}>
          <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 mb-2">{label}</div>
          <div className="space-y-2">
            {list.map(a => {
              const started = !!a.audit_id;
              const closed = a.audit_stage === 'closed';
              return (
                <div key={a.id} className={`bg-white border rounded-xl px-4 py-3 ${closed ? 'border-emerald-200' : 'border-slate-200'}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-semibold text-slate-800">{a.entity_name}</span>
                        <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded border bg-sky-50 text-sky-700 border-sky-200">{TYPE_LABEL[a.activity_type] || a.activity_type}</span>
                        {a.risk_level && <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded border ${RISK_STYLE[a.risk_level] || RISK_STYLE.low}`}>{a.risk_level} risk</span>}
                        {a.scheduled_date && <span className="text-[11px] text-slate-400">{fmtDate(a.scheduled_date)}</span>}
                      </div>
                      {a.title && <div className="text-[12px] text-slate-500 mt-1 line-clamp-1">{a.title}</div>}
                    </div>
                    <div className="flex-shrink-0 text-right">
                      {closed ? (
                        <div className="space-y-1">
                          <span className="inline-block text-[11px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5">
                            Done{a.audit_score != null ? ` · ${a.audit_score}%` : ''}
                          </span>
                          <div><button onClick={() => onOpenAudit(a.audit_id)} className="text-[11px] text-orange-600 hover:underline">View audit</button></div>
                        </div>
                      ) : started ? (
                        <div className="space-y-1">
                          <button onClick={() => onOpenAudit(a.audit_id)} className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-slate-800 text-white shadow">Continue →</button>
                          <div className="text-[10px] text-slate-400">{STAGE_LABELS[a.audit_stage] || a.audit_stage}</div>
                        </div>
                      ) : (
                        <button onClick={() => start(a.id)} disabled={busy === a.id}
                          className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-gradient-to-r from-orange-500 to-orange-600 text-white shadow disabled:opacity-50">
                          {busy === a.id ? 'Setting up…' : 'Start audit'}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}

      {plan.unplanned.length > 0 && (
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 mb-2">
            Not on this year's plan · {plan.unplanned.length} active vendor{plan.unplanned.length === 1 ? '' : 's'}
          </div>
          <div className="space-y-2">
            {plan.unplanned.map(e => (
              <div key={e.id} className="bg-white border border-dashed border-slate-200 rounded-xl px-4 py-3">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="flex items-center gap-2 flex-wrap min-w-0">
                    <span className="text-sm font-semibold text-slate-700">{e.name}</span>
                    {e.risk_level && <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded border ${RISK_STYLE[e.risk_level] || RISK_STYLE.low}`}>{e.risk_level} risk</span>}
                    {e.open_finding_count > 0 && <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded border bg-red-50 text-red-600 border-red-200">{e.open_finding_count} open finding{e.open_finding_count === 1 ? '' : 's'}</span>}
                  </div>
                  {scheduling === e.id ? (
                    <ScheduleInline busy={busy === e.id} onCancel={() => setScheduling(null)} onSave={(type, date) => schedule(e.id, type, date)} />
                  ) : (
                    <button onClick={() => setScheduling(e.id)} className="px-3 py-1.5 text-xs font-semibold rounded-lg border border-slate-300 text-slate-600 hover:bg-slate-50">
                      Put it on the plan
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function ScheduleInline({ busy, onSave, onCancel }) {
  const [type, setType] = useState('desk_audit');
  const [date, setDate] = useState('');
  return (
    <div className="flex items-center gap-2">
      <select value={type} onChange={e => setType(e.target.value)} className="text-xs border border-slate-300 rounded-lg px-2 py-1.5 text-slate-700">
        <option value="desk_audit">Desk audit</option>
        <option value="virtual_audit">Virtual audit</option>
        <option value="site_visit">Site visit</option>
      </select>
      <input type="date" value={date} onChange={e => setDate(e.target.value)} className="text-xs border border-slate-300 rounded-lg px-2 py-1.5 text-slate-700" />
      <button onClick={() => onSave(type, date)} disabled={busy} className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-gradient-to-r from-orange-500 to-orange-600 text-white shadow disabled:opacity-50">
        {busy ? 'Saving…' : 'Schedule'}
      </button>
      <button onClick={onCancel} className="text-[11px] text-slate-400 hover:text-slate-600">Cancel</button>
    </div>
  );
}
